// #383 frontend エラーモニタリング
//
// Sentry init module. main.tsx から initSentry() を一度だけ呼ぶ。
//
// 環境変数:
// - VITE_SENTRY_DSN     未設定なら no-op (= 起動を妨げない)
// - VITE_APP_ENV        "local" | "demo" | "prod" — Sentry の environment tag
// - VITE_SENTRY_RELEASE Sentry release 識別子 (#555)。Source map upload 時の
//                       release name と揃える。未設定時は release tag なしで送信
//
// 設計指針:
// - **DSN の有無だけで有効/無効を決める** — local でも DSN を入れれば動作確認可能
// - DSN 未設定時は init を skip (CI / preview / 開発環境でノイズを出さない)
// - tracesSampleRate=0 で performance monitoring は無効。エラーのみ送信
// - replaysSessionSampleRate=0 / replaysOnErrorSampleRate=0 で session replay も無効
//   （PII / 営業秘密が映り込むリスクを避けるため、有効化は別 issue で要 review）
import * as Sentry from '@sentry/react'

// #640: Sentry payload に secret パターンが混入したら redact する。
// runtime に意図せず log / error message に key が含まれた時の最終防御層。
const SECRET_PATTERNS: RegExp[] = [
  /sk_(live|test)_[A-Za-z0-9]{20,}/g,
  /whsec_[A-Za-z0-9]{20,}/g,
  /AIza[A-Za-z0-9_-]{35}/g,
  /\b0x[a-fA-F0-9]{64}\b/g,
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
]

function redactSecrets(value: unknown, depth = 0): unknown {
  // 深い再帰や循環参照は触らない (Sentry event は深くても 5-6 階層程度)
  if (depth > 8) return value
  if (typeof value === 'string') {
    let s = value
    for (const re of SECRET_PATTERNS) s = s.replace(re, '[REDACTED]')
    return s
  }
  if (Array.isArray(value)) return value.map((v) => redactSecrets(v, depth + 1))
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = redactSecrets(v, depth + 1)
    }
    return out
  }
  return value
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) {
    // 開発 / preview など DSN 未設定の環境では何もしない
    return
  }

  const environment = (import.meta.env.VITE_APP_ENV as string | undefined) || 'unknown'
  const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined

  Sentry.init({
    dsn,
    environment,
    // #555: Source map upload した release name と揃える。これが一致していないと
    // Dashboard で minify 解除後のコード位置が表示されない。
    release: release || undefined,
    // Error 監視のみ。Performance / Replay は無効。
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // #640: payload 内の secret パターンを redact (= 多層防御の最終層)
    beforeSend(event) {
      return redactSecrets(event) as typeof event
    },
  })
}

/**
 * API 呼び出しの失敗を Sentry へ送る (#1624 の議論)。
 *
 * **なぜ必要か**: 画面側は `.catch()` で握って「読み込めませんでした」を出すが、
 * 握った時点で未捕捉例外ではなくなるため、Sentry の既定の捕捉経路
 * (window.onerror / ErrorBoundary) には**一切乗らない**。実際 frontend に
 * captureException は 1 件も無く、**サーバが 500 を返し続けても誰にも届かない**
 * 状態だった。ユーザーの「再読み込み」は index 漏れのような決定論的な失敗には
 * 効かないので、**直すには我々が気付く必要がある**。
 *
 * 送るのは 5xx と通信断だけ。401/403/404/409 等は業務上の想定内で、
 * 送るとノイズになって本当の異常が埋もれる。
 *
 * DSN 未設定 (local / demo) では Sentry.init が no-op なので、この呼び出しも
 * 実質何もしない。
 */
// **同じ失敗を送り続けない。** StoreKitchen と useAutoPrint は 5 秒周期で
// ポーリングしているので、障害中に無制限に送ると Free tier (5K/月) を数時間で
// 使い切り、**本当に拾いたいエラーが落ちる**。同一 (path, status) は 5 分に 1 回。
const REPORT_INTERVAL_MS = 5 * 60_000
const lastReportedAt = new Map<string, number>()
// セッション単位の上限。ポーリング先が多数ある場合の暴走止め
const MAX_REPORTS_PER_SESSION = 50
let reportCount = 0

/** query string を落とし、id 部分を畳む。tag の値に QR トークン等が乗るのを防ぐ */
function tagPath(path: string): string {
  const withoutQuery = path.split('?')[0]
  return withoutQuery
    // 32桁以上の hex / 20文字以上の id っぽい segment を畳む (tag の種類数も抑える)
    .replace(/\/[A-Za-z0-9_-]{20,}/g, '/:id')
}

export function reportApiFailure(
  err: unknown,
  context: { path: string; status?: number },
): void {
  const path = tagPath(context.path)
  const status = String(context.status ?? 'network')
  const key = `${status} ${path}`
  const now = Date.now()
  const prev = lastReportedAt.get(key)
  if (prev !== undefined && now - prev < REPORT_INTERVAL_MS) return
  if (reportCount >= MAX_REPORTS_PER_SESSION) return
  lastReportedAt.set(key, now)
  reportCount += 1

  Sentry.captureException(err, {
    tags: { kind: 'api_failure', api_path: path, api_status: status },
  })
}

export { Sentry }
